import { Presence } from '@radix-ui/react-presence';
import { type CSSProperties, memo, type ReactNode } from 'react';

import { type WorkspaceAgentPanelSlotWidthAnimationStyle } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentPanelDockedLayout';

export type WorkspaceAgentPanelSlotClipProps = {
  children: ReactNode;
  isSlotOpen: boolean;
  shouldAnimateSlot: boolean;
  slotClipStyle: CSSProperties | null;
  slotWidthAnimationStyle: WorkspaceAgentPanelSlotWidthAnimationStyle;
};

type WorkspaceAgentPanelSlotClipDataState = 'closed' | 'open';

type GetWorkspaceAgentPanelSlotClipDataStateParams = {
  isClipped: boolean;
  isSlotOpen: boolean;
};

const getWorkspaceAgentPanelSlotClipDataState = ({
  isClipped,
  isSlotOpen,
}: GetWorkspaceAgentPanelSlotClipDataStateParams): WorkspaceAgentPanelSlotClipDataState | undefined => {
  if (!isClipped) {
    return undefined;
  }

  if (isSlotOpen) {
    return 'open';
  }

  return 'closed';
};

/**
 * Overflow clip around the portalled card for CSS width-in / width-out.
 *
 * The wrapper tree is always the same (`Presence` → clip → inner width) so toggling `slotClipStyle`
 * (docked ↔ float, asset view, pre-ui2026) cannot remount `children`. Drag-to-undock, TipTap, the
 * composer draft, and thread scroll all live in that child; swapping the tree would kill them.
 *
 * When there is no clip geometry, Presence stays `present` and the divs are unstyled pass-throughs
 * so a viewport-fixed card is not overflow-hidden or width-animated.
 */
export const WorkspaceAgentPanelSlotClip = memo(
  ({
    children,
    isSlotOpen,
    shouldAnimateSlot,
    slotClipStyle,
    slotWidthAnimationStyle,
  }: WorkspaceAgentPanelSlotClipProps) => {
    const isClipped = slotClipStyle !== null;

    return (
      <Presence present={isSlotOpen || !isClipped}>
        <div
          className={
            isClipped
              ? 'animation-duration-150 fixed z-1 shrink-0 overflow-hidden data-[animate=true]:motion-safe:radix-state-closed:animate-width-out data-[animate=true]:motion-safe:radix-state-open:animate-width-in'
              : undefined
          }
          data-animate={isClipped && shouldAnimateSlot ? true : undefined}
          data-state={getWorkspaceAgentPanelSlotClipDataState({ isClipped, isSlotOpen })}
          style={
            isClipped
              ? {
                  ...slotClipStyle,
                  ...slotWidthAnimationStyle,
                }
              : undefined
          }
        >
          <div className={isClipped ? 'h-full w-[var(--workspace-agent-panel-width)]' : undefined}>{children}</div>
        </div>
      </Presence>
    );
  },
);

WorkspaceAgentPanelSlotClip.displayName = 'WorkspaceAgentPanelSlotClip';
